import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
const Logo = () => <Link to="/HomePage#" className="flex items-center gap-2 z-50">
    <img src="https://notable-crane.10web.cloud/wp-content/uploads/2026/02/cropped-Bild-12.02.26-um-17.20.jpeg" alt="Leci Bau GmbH & Co.KG" className="!w-[60px] !h-[60px]" />
  </Link>;
const MenuLinkStyles = "relative text-sm font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary group py-2";
const UnderlineStyles = "absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const defaultItems = [{
    id: 'd1',
    label: 'Leistungen',
    href: '#leistungen'
  }, {
    id: 'd2',
    label: 'Über Uns',
    href: '#ueber-uns'
  }, {
    id: 'd3',
    label: 'Kontakt',
    href: '#kontakt'
  }];
  const rawItems = menuItems && menuItems.length > 0 ? menuItems : defaultItems;
  const items = rawItems.filter(item => !["Team", "Projekte", "Leistungen", "Über"].includes(item.label) && item.href !== "/#über-uns");
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={MenuLinkStyles}>
                {item.label}
                <span className={UnderlineStyles} />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const defaultItems = [{
    id: 'm1',
    label: 'Leistungen',
    href: '#leistungen'
  }, {
    id: 'm2',
    label: 'Über Uns',
    href: '#ueber-uns'
  }, {
    id: 'm3',
    label: 'Kontakt',
    href: '#kontakt'
  }];
  const rawItems = menuItems && menuItems.length > 0 ? menuItems : defaultItems;
  const items = rawItems.filter(item => !["Team", "Projekte", "Leistungen", "Über"].includes(item.label) && item.href !== "/#über-uns");
  if (loading) {
    return <div className="flex flex-col gap-6 p-8">
        {[1, 2, 3].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center gap-8 p-8">
      {items.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-medium uppercase tracking-widest text-foreground hover:text-primary transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex-shrink-0">
          <Logo />
        </div>

        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="3">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Open menu">
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      <div className={`fixed inset-0 z-50 bg-background/95 backdrop-blur-xl transition-transform duration-300 md:hidden ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 h-20 border-b border-border/10">
            <Logo />
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Close menu">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <MenuProvider menu_id="3">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>
      </div>
    </header>;
}